import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { DoBoxEntryKind, InboxEntry } from '@this+that/models';
import log from 'loglevel';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import {
  useConvertInboxEntryKind,
  useSetInboxEntryParent,
} from '../../services/inboxEntryTree.service';
import { resolveTaskTreeErrorKey } from '../../utils/taskTree/resolveTaskTreeErrorKey';
import { useCustomToast } from '../useCustomToast';

const CONVERT_SUCCESS_KEY: Partial<Record<DoBoxEntryKind, string>> = {
  [DoBoxEntryKind.SPEC]: 'doBox.spec.convertedToSpec',
  [DoBoxEntryKind.TASK]: 'doBox.spec.convertedToTask',
};

export function useTaskTreeActions(inboxEntry: InboxEntry) {
  const { t } = useTranslation();
  const { successMessage, errorMessage } = useCustomToast();
  const {
    updateInboxEntry,
    saveDetailedInboxEntry,
    removeInboxEntryFromActiveList,
  } = useActiveInboxEntriesSlice();
  const { convertInboxEntryKind, loading: isConverting } =
    useConvertInboxEntryKind();
  const { setInboxEntryParent, loading: isSettingParent } =
    useSetInboxEntryParent();

  const convertKind = useCallback(
    async (entryKind: DoBoxEntryKind.TASK | DoBoxEntryKind.SPEC) => {
      try {
        const updated = await convertInboxEntryKind({
          inboxEntryId: inboxEntry.id,
          entryKind,
        });
        if (updated) {
          updateInboxEntry(updated);
          successMessage(t(CONVERT_SUCCESS_KEY[entryKind] as string));
        }
        return Boolean(updated);
      } catch (error) {
        log.error('Error converting inbox entry kind:', error);
        errorMessage(t(resolveTaskTreeErrorKey(error)));
        return false;
      }
    },
    [
      convertInboxEntryKind,
      inboxEntry.id,
      updateInboxEntry,
      successMessage,
      errorMessage,
      t,
    ],
  );

  const setParent = useCallback(
    async (parentInboxEntryId: string | null) => {
      try {
        const updated = await setInboxEntryParent({
          inboxEntryId: inboxEntry.id,
          parentInboxEntryId,
        });
        if (!updated) {
          return false;
        }
        if (parentInboxEntryId) {
          removeInboxEntryFromActiveList(updated);
        } else {
          saveDetailedInboxEntry(updated);
        }
        return true;
      } catch (error) {
        log.error('Error setting inbox entry parent:', error);
        errorMessage(t(resolveTaskTreeErrorKey(error)));
        return false;
      }
    },
    [
      setInboxEntryParent,
      inboxEntry.id,
      removeInboxEntryFromActiveList,
      saveDetailedInboxEntry,
      errorMessage,
      t,
    ],
  );

  return { convertKind, setParent, isConverting, isSettingParent };
}
